import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Alert } from "../ui/alert";
import { Spinner } from "../ui/spinner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Avatar, AvatarImage, AvatarFallback } from "../ui/avatar";
import { Badge } from "../ui/badge";
import { Search, Users, Check, Mail, UserPlus } from "lucide-react";
import { getApiUrl } from "../../config/api";
import SendInviteDialog from "../Invites/SendInviteDialog";

const AddUserToBusinessUnitUnified = ({
  businessUnitId,
  businessUnit,
  onUserAdded,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const searchUsers = async () => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        getApiUrl(`users/search?q=${encodeURIComponent(searchTerm)}`),
        {
          headers: {
            Authorization: `Bearer ${await getAccessTokenSilently()}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to search users");
      }

      const data = await response.json();
      setSearchResults(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const assignBusinessUser = async (userId) => {
    try {
      setAssignLoading(true);
      setError(null);

      const response = await fetch(
        getApiUrl(`business-units/${businessUnitId}/assign-business-user`),
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${await getAccessTokenSilently()}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ user_id: userId }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to assign business user");
      }

      setSuccess("Business user assigned successfully!");
      setSearchTerm("");
      setSearchResults([]);

      if (onUserAdded) {
        onUserAdded();
      }

      // Close dialog after successful assignment
      setTimeout(() => {
        setIsOpen(false);
        setSuccess(null);
      }, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setAssignLoading(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchUsers();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const resetDialog = () => {
    setSearchTerm("");
    setSearchResults([]);
    setError(null);
    setSuccess(null);
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
        if (!open) resetDialog();
      }}
    >
      <DialogTrigger asChild>
        <Button className="flex items-center gap-2">
          <UserPlus className="h-4 w-4" />
          Add Business User
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add Business User</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Search for existing users or invite new ones to join "
            {businessUnit?.name}" as business users
          </p>
        </DialogHeader>

        <div className="space-y-4">
          {error && <Alert variant="destructive">{error}</Alert>}

          {success && (
            <Alert className="border-green-200 bg-green-50 text-green-800">
              <Check className="h-4 w-4" />
              {success}
            </Alert>
          )}

          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search users by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Search Results */}
          <div className="max-h-60 overflow-y-auto">
            {loading ? (
              <div className="flex justify-center py-4">
                <Spinner />
              </div>
            ) : searchResults.length > 0 ? (
              <div className="space-y-2">
                {searchResults.map((user) => (
                  <Card key={user.id} className="p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={user.picture} />
                          <AvatarFallback>
                            {user.name
                              ?.split(" ")
                              .map((n) => n[0])
                              .join("")}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-sm font-medium">{user.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {user.email}
                          </p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => assignBusinessUser(user.id)}
                        disabled={assignLoading}
                        className="flex items-center gap-1"
                      >
                        {assignLoading ? (
                          <Spinner size="sm" />
                        ) : (
                          <Users className="h-3 w-3" />
                        )}
                        Add as Business User
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            ) : searchTerm.trim() && !loading ? (
              <div className="text-center py-4 text-muted-foreground">
                <div className="space-y-3">
                  <p>No users found matching "{searchTerm}"</p>
                  <div className="text-xs text-muted-foreground">
                    Can't find the user? You can send them an invitation
                    instead.
                  </div>
                  <SendInviteDialog
                    organizationId={businessUnit?.organization_id}
                    businessUnitId={businessUnitId}
                    businessUnit={businessUnit}
                    defaultRole="business_user"
                    onInviteSent={onUserAdded}
                  >
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-2"
                    >
                      <Mail className="h-4 w-4" />
                      Send Invitation
                    </Button>
                  </SendInviteDialog>
                </div>
              </div>
            ) : null}
          </div>

          {searchTerm.trim() === "" && (
            <div className="text-center py-4 text-muted-foreground text-sm space-y-3">
              <div className="flex items-center justify-center gap-2">
                <Search className="h-4 w-4" />
                <span>Start typing to search for existing users</span>
              </div>
              <div className="text-xs">Or send an invitation to a new user</div>
              <SendInviteDialog
                organizationId={businessUnit?.organization_id}
                businessUnitId={businessUnitId}
                businessUnit={businessUnit}
                defaultRole="business_user"
                onInviteSent={onUserAdded}
              >
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2"
                >
                  <UserPlus className="h-4 w-4" />
                  Invite New User
                </Button>
              </SendInviteDialog>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddUserToBusinessUnitUnified;
